import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Horse } from '@phosphor-icons/react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { SMART_COLORS as COLORS, smartCardSx as cardSx } from '../../theme/smartStableTheme';

const apiUrl = process.env.REACT_APP_API_URL;

const COPY = {
  ar: {
    title: 'الجولة البيطرية',
    subtitle: 'لوحة سريعة لمعاينة الخيل حسب الإسطبل والغرفة وتسجيل الحالات أثناء الجولة.',
    search: 'بحث باسم الغرفة أو الخيل',
    refresh: 'تحديث',
    clear: 'مسح التحديد',
    allStables: 'كل الإسطبلات',
    rooms: 'الغرف',
    occupiedRooms: 'غرف بها خيل',
    openCases: 'حالات مفتوحة',
    horses: 'الخيل',
    stable: 'الإسطبل',
    room: 'غرفة',
    available: 'فارغة',
    occupied: 'مشغولة',
    maintenance: 'صيانة',
    noHorse: 'لا يوجد خيل في الغرفة',
    addCheckup: 'تسجيل معاينة',
    selectHorse: 'اختر الخيل',
    severity: 'الشدة',
    cases: 'الحالات',
    followUp: 'تاريخ المتابعة',
    actionRequired: 'يتطلب إجراء',
    itemNotes: 'ملاحظات الحالة',
    roundNotes: 'ملاحظات الجولة',
    low: 'منخفض',
    medium: 'متوسط',
    high: 'عال',
    critical: 'حرج',
    save: 'حفظ الجولة',
    cancel: 'إلغاء',
    loading: 'جاري التحميل...',
    noOptions: 'لا توجد خيارات',
    noRooms: 'لا توجد غرف متاحة للعرض.',
    loadFailed: 'فشل تحميل البيانات',
    login: 'الرجاء تسجيل الدخول',
    roomMissing: 'بيانات الغرفة غير مكتملة',
    horseRequired: 'اختر الخيل',
    typesRequired: 'اختر حالة واحدة على الأقل',
    saved: 'تم الحفظ بنجاح',
    saveFailed: 'فشل الحفظ',
    checklistHorse: 'الخيل',
    checklistCases: 'الحالات',
    checklistAction: 'الإجراء',
    selectedCases: 'الحالات المختارة',
  },
  en: {
    title: 'Veterinary Round',
    subtitle: 'A focused daily board for checking horses by stable and room, then recording findings during the round.',
    search: 'Search by room or horse',
    refresh: 'Refresh',
    clear: 'Clear',
    allStables: 'All stables',
    rooms: 'Rooms',
    occupiedRooms: 'Occupied rooms',
    openCases: 'Open cases',
    horses: 'Horses',
    stable: 'Stable',
    room: 'Room',
    available: 'Empty',
    occupied: 'Occupied',
    maintenance: 'Maintenance',
    noHorse: 'No horse in this room',
    addCheckup: 'Record Checkup',
    selectHorse: 'Select horse',
    severity: 'Severity',
    cases: 'Cases',
    followUp: 'Follow-up date',
    actionRequired: 'Action required',
    itemNotes: 'Case notes',
    roundNotes: 'Round notes',
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    critical: 'Critical',
    save: 'Save round',
    cancel: 'Cancel',
    loading: 'Loading...',
    noOptions: 'No options',
    noRooms: 'No rooms to show.',
    loadFailed: 'Failed to load data',
    login: 'Please log in',
    roomMissing: 'Room data is incomplete',
    horseRequired: 'Select a horse',
    typesRequired: 'Select at least one case',
    saved: 'Saved successfully',
    saveFailed: 'Save failed',
    checklistHorse: 'Horse',
    checklistCases: 'Cases',
    checklistAction: 'Action',
    selectedCases: 'Selected cases',
  },
};

const severityMeta = {
  low: { color: COLORS.green, soft: COLORS.greenSoft },
  medium: { color: COLORS.gold, soft: COLORS.goldSoft },
  high: { color: '#c29355', soft: COLORS.warningSoft },
  critical: { color: COLORS.rose, soft: COLORS.roseSoft },
};

function roomState(room) {
  if (room.status === 'maintenance') return 'maintenance';
  if (Number(room.accommodation_count || 0) > 0) return 'occupied';
  return 'available';
}

function roomStyle(room) {
  const state = roomState(room);
  if (state === 'maintenance') return { color: COLORS.rose, soft: COLORS.roseSoft, labelKey: 'maintenance' };
  if (state === 'occupied') return { color: COLORS.blue, soft: COLORS.blueSoft, labelKey: 'occupied' };
  return { color: COLORS.green, soft: COLORS.greenSoft, labelKey: 'available' };
}

function splitNames(value) {
  return String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function MetricCard({ icon, title, value, helper, color = COLORS.gold, soft = COLORS.goldSoft }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2, minHeight: 104 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar sx={{ bgcolor: soft, color, width: 48, height: 48 }}>{icon}</Avatar>
        <Box>
          <Typography sx={{ color, fontWeight: 800 }}>{title}</Typography>
          <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900 }}>{value}</Typography>
          {helper && <Typography variant="caption" sx={{ color: COLORS.muted }}>{helper}</Typography>}
        </Box>
      </Stack>
    </Paper>
  );
}

function RoomCard({ room, text, onClick }) {
  const style = roomStyle(room);
  const horses = splitNames(room.horse_names);
  const capacity = Number(room.capacity || 0);
  const used = Number(room.accommodation_count || horses.length || 0);
  const percent = capacity ? Math.min(100, Math.round((used / capacity) * 100)) : used ? 100 : 0;

  return (
    <Badge
      color="error"
      overlap="rectangular"
      badgeContent={Number(room.open_checkups_count || 0) || null}
      sx={{ display: 'block', width: '100%' }}
    >
      <Paper
        elevation={0}
        onClick={onClick}
        sx={{
          ...cardSx,
          p: 1.5,
          minHeight: 178,
          cursor: 'pointer',
          borderColor: style.color,
          borderTop: `5px solid ${style.color}`,
          bgcolor: style.soft,
          transition: 'transform .16s ease, box-shadow .16s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 12px 28px rgba(91, 75, 39, 0.12)',
          },
        }}
      >
        <Stack spacing={1.2} sx={{ height: '100%' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900, lineHeight: 1 }}>
                {room.room_name}
              </Typography>
              <Typography variant="caption" sx={{ color: COLORS.muted }}>
                {room.stable_name}
              </Typography>
            </Box>
            <Chip
              size="small"
              label={text[style.labelKey]}
              sx={{ bgcolor: '#fff', color: style.color, border: `1px solid ${style.color}`, fontWeight: 800 }}
            />
          </Stack>

          <Box>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption" sx={{ color: COLORS.muted }}>{text.horses}</Typography>
              <Typography variant="caption" sx={{ color: COLORS.text, fontWeight: 900 }}>{used}/{capacity || '-'}</Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={percent}
              sx={{
                mt: 0.5,
                height: 7,
                borderRadius: 99,
                bgcolor: 'rgba(255,255,255,0.75)',
                '& .MuiLinearProgress-bar': { bgcolor: style.color, borderRadius: 99 },
              }}
            />
          </Box>

          <Stack direction="row" flexWrap="wrap" gap={0.6} sx={{ mt: 'auto' }}>
            {horses.length ? (
              horses.slice(0, 3).map((horse, index) => (
                <Chip key={`${room.room_id}-${horse}-${index}`} size="small" icon={<Horse size={16} weight="fill" />} label={horse} sx={{ bgcolor: '#fff', maxWidth: '100%' }} />
              ))
            ) : (
              <Typography variant="caption" sx={{ color: COLORS.muted }}>{text.noHorse}</Typography>
            )}
            {horses.length > 3 && <Chip size="small" label={`+${horses.length - 3}`} sx={{ bgcolor: '#fff' }} />}
          </Stack>
        </Stack>
      </Paper>
    </Badge>
  );
}

export default function VetCheckupView() {
  const { i18n: i18nCtx } = useTranslation();
  const text = COPY[i18nCtx.language === 'ar' ? 'ar' : 'en'];

  const [rooms, setRooms] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(true);
  const [roomsError, setRoomsError] = useState('');
  const [selectedStables, setSelectedStables] = useState([]);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [roomHorses, setRoomHorses] = useState([]);
  const [loadingHorses, setLoadingHorses] = useState(false);
  const [types, setTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [selectedHorse, setSelectedHorse] = useState('');
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [severity, setSeverity] = useState('low');
  const [actionRequired, setActionRequired] = useState(true);
  const today = new Date().toISOString().split('T')[0];
  const [followUpDate, setFollowUpDate] = useState(today);
  const [itemNotes, setItemNotes] = useState('');
  const [roundNotes, setRoundNotes] = useState('');
  const vetUserId = Number(localStorage.getItem('userId') || 0);
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', sev: 'success' });

  const loadRooms = useCallback(async () => {
    setLoadingRooms(true);
    setRoomsError('');
    try {
      const response = await axios.get(`${apiUrl}/rooms/rooms-with-stable-count`);
      setRooms(Array.isArray(response.data) ? response.data : []);
    } catch {
      setRoomsError(text.loadFailed);
    } finally {
      setLoadingRooms(false);
    }
  }, [text.loadFailed]);

  const loadTypes = useCallback(async () => {
    setLoadingTypes(true);
    try {
      const response = await axios.get(`${apiUrl}/checkup/types/active`);
      const data = response.data;
      setTypes(Array.isArray(data) ? data : data ? [data] : []);
    } catch {
      setTypes([]);
    } finally {
      setLoadingTypes(false);
    }
  }, []);

  useEffect(() => {
    loadRooms();
    loadTypes();
  }, [loadRooms, loadTypes]);

  const groupedByStable = useMemo(() => {
    const acc = {};
    rooms.forEach((room) => {
      (acc[room.stable_name || '-'] ||= []).push(room);
    });
    return acc;
  }, [rooms]);

  const stableNames = useMemo(
    () => Object.keys(groupedByStable).sort((a, b) => String(a).localeCompare(String(b), 'ar', { sensitivity: 'base' })),
    [groupedByStable]
  );

  const filteredStableNames = useMemo(
    () => stableNames.filter((name) => selectedStables.length === 0 || selectedStables.includes(name)),
    [selectedStables, stableNames]
  );

  const applySearch = useCallback(
    (list) => {
      const needle = search.trim().toLowerCase();
      if (!needle) return list;
      return list.filter((room) =>
        [room.room_name, room.stable_name, room.horse_names].some((value) =>
          String(value || '').toLowerCase().includes(needle)
        )
      );
    },
    [search]
  );

  const summary = useMemo(() => {
    const occupied = rooms.filter((room) => roomState(room) === 'occupied').length;
    const openCases = rooms.reduce((sum, room) => sum + Number(room.open_checkups_count || 0), 0);
    const horses = rooms.reduce((sum, room) => sum + Number(room.accommodation_count || splitNames(room.horse_names).length || 0), 0);
    return { occupied, openCases, horses };
  }, [rooms]);

  const toggleStable = (name) => {
    setSelectedStables((prev) => (prev.includes(name) ? prev.filter((item) => item !== name) : [...prev, name]));
  };

  const openRoomDialog = useCallback(
    async (room) => {
      setCurrentRoom(room);
      setDialogOpen(true);
      setSelectedHorse('');
      setSelectedTypes([]);
      setSeverity('low');
      setActionRequired(true);
      setFollowUpDate(today);
      setItemNotes('');
      setRoundNotes('');

      setLoadingHorses(true);
      try {
        const response = await axios.get(`${apiUrl}/rooms/${room.room_id}/horses`);
        setRoomHorses(Array.isArray(response.data) ? response.data : []);
      } catch {
        setRoomHorses([]);
      } finally {
        setLoadingHorses(false);
      }
    },
    [today]
  );

  const groupedTypes = useMemo(() => {
    const byCategory = {};
    types.forEach((item) => {
      const key = i18n.language === 'ar' ? item.category_ar : item.category_en;
      (byCategory[key || '-'] ||= []).push(item);
    });
    return byCategory;
  }, [types]);

  const flatTypes = useMemo(
    () => Object.entries(groupedTypes).flatMap(([category, list]) => list.map((item) => ({ ...item, _group: category }))),
    [groupedTypes]
  );

  const handleSave = useCallback(async () => {
    if (!vetUserId) {
      setSnackbar({ open: true, msg: text.login, sev: 'error' });
      return;
    }
    if (!currentRoom?.room_id || !currentRoom?.stable_id) {
      setSnackbar({ open: true, msg: text.roomMissing, sev: 'error' });
      return;
    }
    if (!selectedHorse) {
      setSnackbar({ open: true, msg: text.horseRequired, sev: 'error' });
      return;
    }
    if (!selectedTypes.length) {
      setSnackbar({ open: true, msg: text.typesRequired, sev: 'error' });
      return;
    }

    const payload = {
      room_id: currentRoom.room_id,
      stable_id: currentRoom.stable_id,
      visit_date: today,
      vet_user_id: vetUserId,
      round_notes: roundNotes || null,
      notes: roundNotes || null,
      items: selectedTypes.map((item) => ({
        horse_id: selectedHorse,
        type_id: item.type_id,
        severity,
        action_required: actionRequired ? 1 : 0,
        follow_up_date: followUpDate || null,
        notes: itemNotes || null,
      })),
    };

    try {
      await axios.post(`${apiUrl}/checkup/rounds`, payload);
      setSnackbar({ open: true, msg: text.saved, sev: 'success' });
      setDialogOpen(false);
      loadRooms();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || text.saveFailed;
      setSnackbar({ open: true, msg, sev: 'error' });
    }
  }, [actionRequired, currentRoom, followUpDate, itemNotes, loadRooms, roundNotes, selectedHorse, selectedTypes, severity, text, today, vetUserId]);

  const selectedSeverity = severityMeta[severity] || severityMeta.low;

  return (
    <Box
      className="smart-page-container"
      sx={{
        minHeight: '100vh',
        bgcolor: COLORS.page,
        width: '100%',
        maxWidth: 'var(--app-page-max)',
        mx: 'auto',
        overflowX: 'hidden',
        pb: 6,
      }}
    >
      <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 3 }, mb: 3 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'center' }}>
          <Box>
            <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>{text.title}</Typography>
            <Typography sx={{ color: COLORS.muted, mt: 0.5, maxWidth: 780 }}>{text.subtitle}</Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              size="small"
              placeholder={text.search}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              sx={{ width: { xs: '100%', sm: 320 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title={text.refresh}>
              <IconButton onClick={loadRooms} sx={{ border: `1px solid ${COLORS.line}`, color: COLORS.gold }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' }, gap: 2, mb: 3, width: '100%' }}>
        <MetricCard icon={<HomeWorkIcon />} title={text.rooms} value={rooms.length} helper={text.allStables} />
        <MetricCard icon={<Horse size={26} weight="fill" />} title={text.horses} value={summary.horses} color={COLORS.blue} soft={COLORS.blueSoft} />
        <MetricCard icon={<HealthAndSafetyIcon />} title={text.occupiedRooms} value={summary.occupied} color={COLORS.green} soft={COLORS.greenSoft} />
        <MetricCard icon={<WarningAmberIcon />} title={text.openCases} value={summary.openCases} color={COLORS.rose} soft={COLORS.roseSoft} />
      </Box>

      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 3 }}>
        {stableNames.map((name) => {
          const active = selectedStables.includes(name);
          return (
            <Chip
              key={name}
              label={name}
              onClick={() => toggleStable(name)}
              onDelete={active ? () => toggleStable(name) : undefined}
              sx={{
                bgcolor: active ? COLORS.gold : COLORS.card,
                color: active ? '#fff' : COLORS.text,
                border: `1px solid ${active ? COLORS.gold : COLORS.line}`,
                fontWeight: 800,
              }}
            />
          );
        })}
        {selectedStables.length > 0 && <Button size="small" onClick={() => setSelectedStables([])}>{text.clear}</Button>}
      </Stack>

      {loadingRooms ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
            gap: 2,
            width: '100%',
          }}
        >
          {Array.from({ length: 12 }).map((_, index) => (
            <Box key={index} sx={{ minWidth: 0 }}>
              <Skeleton variant="rounded" height={178} />
            </Box>
          ))}
        </Box>
      ) : roomsError ? (
        <Alert severity="error">{roomsError}</Alert>
      ) : filteredStableNames.length === 0 ? (
        <Alert severity="info">{text.noRooms}</Alert>
      ) : (
        filteredStableNames.map((stableName) => {
          const stableRooms = [...(groupedByStable[stableName] || [])].sort((a, b) =>
            String(a.room_name).localeCompare(String(b.room_name), 'ar', { numeric: true })
          );
          const visibleRooms = applySearch(stableRooms);
          if (!visibleRooms.length) return null;

          return (
            <Box key={stableName} sx={{ mb: 4 }}>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>
                  {text.stable}: {stableName}
                </Typography>
                <Chip size="small" label={`${visibleRooms.length} ${text.rooms}`} sx={{ bgcolor: COLORS.goldSoft, color: COLORS.text, fontWeight: 800 }} />
                <Divider sx={{ flexGrow: 1 }} />
              </Stack>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
                  gap: 2,
                  width: '100%',
                }}
              >
                {visibleRooms.map((room) => (
                  <Box key={room.room_id} sx={{ minWidth: 0 }}>
                    <RoomCard room={room} text={text} onClick={() => openRoomDialog(room)} />
                  </Box>
                ))}
              </Box>
            </Box>
          );
        })
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: COLORS.greenSoft, color: COLORS.green }}>
              <MedicalServicesIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>{text.addCheckup}</Typography>
              <Typography variant="caption" sx={{ color: COLORS.muted }}>
                {currentRoom?.stable_name} · {text.room} {currentRoom?.room_name}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '260px 1fr' }, gap: 2 }}>
            <Paper elevation={0} sx={{ ...cardSx, p: 2, bgcolor: COLORS.cardSoft }}>
              <Stack spacing={1.5}>
                <Typography sx={{ fontWeight: 900, color: COLORS.text }}>{text.selectedCases}</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Checkbox checked={Boolean(selectedHorse)} disabled />
                  <Typography variant="body2">{text.checklistHorse}</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Checkbox checked={selectedTypes.length > 0} disabled />
                  <Typography variant="body2">{text.checklistCases}</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Checkbox checked={actionRequired} disabled />
                  <Typography variant="body2">{text.checklistAction}</Typography>
                </Stack>
                <Divider />
                <Chip
                  icon={<EventRepeatIcon />}
                  label={followUpDate || '-'}
                  sx={{ bgcolor: '#fff', justifyContent: 'flex-start' }}
                />
                <Chip
                  icon={<AssignmentTurnedInIcon />}
                  label={text[severity] || severity}
                  sx={{ bgcolor: selectedSeverity.soft, color: selectedSeverity.color, fontWeight: 900, justifyContent: 'flex-start' }}
                />
              </Stack>
            </Paper>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{text.selectHorse}</InputLabel>
                  <Select label={text.selectHorse} value={selectedHorse} onChange={(event) => setSelectedHorse(event.target.value)}>
                    {loadingHorses ? (
                      <MenuItem disabled>{text.loading}</MenuItem>
                    ) : roomHorses.length ? (
                      roomHorses.map((horse) => (
                        <MenuItem key={horse.horse_id} value={horse.horse_id}>
                          {horse.name || horse.name_arabic}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>{text.noHorse}</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{text.severity}</InputLabel>
                  <Select label={text.severity} value={severity} onChange={(event) => setSeverity(event.target.value)}>
                    {Object.keys(severityMeta).map((key) => (
                      <MenuItem key={key} value={key}>{text[key]}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={flatTypes}
                  groupBy={(option) => option._group}
                  getOptionLabel={(option) => (i18n.language === 'ar' ? option.name_ar : option.name_en) || ''}
                  noOptionsText={text.noOptions}
                  value={selectedTypes}
                  onChange={(_, value) => setSelectedTypes(value)}
                  loading={loadingTypes}
                  renderInput={(params) => <TextField {...params} label={text.cases} />}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label={text.followUp}
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  value={followUpDate}
                  onChange={(event) => setFollowUpDate(event.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 1.3, border: `1px solid ${COLORS.line}`, borderRadius: 2, height: '100%' }}>
                  <FormControlLabel
                    control={<Switch checked={actionRequired} onChange={(event) => setActionRequired(event.target.checked)} />}
                    label={text.actionRequired}
                  />
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <TextField label={text.itemNotes} multiline minRows={5} fullWidth value={itemNotes} onChange={(event) => setItemNotes(event.target.value)} />
              </Grid>

              <Grid item xs={12}>
                <TextField label={text.roundNotes} multiline minRows={2} fullWidth value={roundNotes} onChange={(event) => setRoundNotes(event.target.value)} />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: COLORS.text }}>{text.cancel}</Button>
          <Button variant="contained" onClick={handleSave} sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover } }}>
            {text.save}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} onClose={() => setSnackbar((state) => ({ ...state, open: false }))} autoHideDuration={snackbar.sev === 'error' ? 5000 : 3000}>
        <Alert severity={snackbar.sev} onClose={() => setSnackbar((state) => ({ ...state, open: false }))}>
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
